import {useState} from "react";
import {annualizedDsSalary, dsSalaryCoverage} from "@/utils/clubSnapshot";
import {dsMembershipLabel} from "@/utils/clubSnapshot";
import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsChips,
	DsEmpty,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsSection,
	DsStatus,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubSnapshot} from "@/utils/hooks";
import {
	buildNeeds,
	buildSeasonPlan,
	formatEuro,
	playerName,
} from "@/utils/clubSnapshot";

const BUCKETS = [
	{id: "3m", label: "Entro 3 mesi", from: 0, to: 90, status: "alert"},
	{id: "6m", label: "Da 3 a 6 mesi", from: 91, to: 180, status: "warn"},
	{id: "12m", label: "Da 6 a 12 mesi", from: 181, to: 365, status: "none"},
];

const CONTRACT_TYPES = {
	professional: "Professionistico",
	amateur: "Dilettantistico",
	youth: "Settore giovanile",
	loan: "Prestito",
	amateur_professional: "Semiprofessionistico",
};

function DsMarketPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});

	const contracts = s?.contracts || [];
	const inBucket = (b) =>
		contracts.filter(
			(c) => c.daysLeft !== null && c.daysLeft >= b.from && c.daysLeft <= b.to,
		);
	const needs = s ? buildNeeds(s) : [];

	// Prossime tre stagioni dopo quella in corso
	const firstYear = s ? moment(s.seasonEnd).year() : moment().year();
	const planYears = [0, 1, 2].map((i) => firstYear + i);
	const [planYear, setPlanYear] = useState(null);
	const year = planYear || planYears[0];
	const plan = s ? buildSeasonPlan(s, year) : [];

	const contractColumns = [
		{
			key: "player",
			label: "Giocatore",
			render: (c) => (
				<>
					<div className="ds-strong">
						{c.playerFirstName} {c.playerLastName}
					</div>
					<div className="ds-muted">{dsMembershipLabel(c) || "—"}</div>
				</>
			),
		},
		{
			key: "type",
			label: "Tipo",
			render: (c) => CONTRACT_TYPES[c.contractType] || c.contractType || "—",
		},
		{
			key: "end",
			label: "Scadenza",
			render: (c) => (c.endDate ? moment(c.endDate).format("D MMM YYYY") : "—"),
		},
		{
			key: "days",
			label: "Giorni",
			align: "center",
			render: (c) => c.daysLeft,
		},
		{
			key: "salary",
			label: "Ingaggio/mese",
			align: "right",
			render: (c) => formatEuro(c.totalMonthlySalary),
		},
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Mercato & contratti"
					subtitle="Contratti dell’elenco Finanza alla data odierna; la stagione seleziona le persone. Fabbisogni sperimentali DS."
					snapshot={s}
					scope={scope}
					area="mercato"
					onReload={reload}
					reloading={loading}
					actions={
						<button
							className="ds-btn ds-btn--ghost"
							onClick={() => navigate("/manager/market-simulation")}
						>
							Simulazione mercato
						</button>
					}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi
								label="Contratti elenco Finanza"
								value={contracts.length}
							/>
							{BUCKETS.map((b) => (
								<DsKpi
									key={b.id}
									label={b.label}
									value={inBucket(b).length}
									tone={
										inBucket(b).length && b.status === "alert"
											? "red"
											: inBucket(b).length && b.status === "warn"
												? "yellow"
												: undefined
									}
									sub="in scadenza"
								/>
							))}
							<DsKpi
								label="Monte ingaggi"
								value={formatEuro(s.monthlySalary)}
								sub={`al mese · ${dsSalaryCoverage(s)} · ${formatEuro(annualizedDsSalary(s.monthlySalary))} annualizzati (mensile × 12)`}
							/>
						</DsKpiRow>

						<DsSection
							title="Fabbisogni per reparto"
							hint="Scenario sperimentale DS, non requisito di organico. Fine stagione stimata solo quando tutti i contratti della persona scadono entro il termine."
							action={<DsLink to="/ds/scouting">Candidati in scouting</DsLink>}
						>
							{needs.length ? (
								<DsTable
									columns={[
										{
											key: "team",
											label: "Squadra",
											render: (n) => <b>{n.team.name}</b>,
										},
										{
											key: "dep",
											label: "Reparto",
											render: (n) => n.department.label,
										},
										{
											key: "now",
											label: "Oggi",
											align: "center",
											render: (n) => `${n.now}/${n.department.min}`,
										},
										{
											key: "next",
											label: "Fine stagione",
											align: "center",
											render: (n) => (
												<DsStatus
													status={n.severity}
													label={`${n.next}/${n.department.min}`}
												/>
											),
										},
										{
											key: "leaving",
											label: "In scadenza",
											render: (n) =>
												n.leaving.length
													? n.leaving.map((p) => p.lastName).join(", ")
													: "—",
										},
										{
											key: "missing",
											label: "Da integrare",
											align: "center",
											render: (n) => <b>{n.missing || "—"}</b>,
										},
									]}
									rows={needs}
								/>
							) : (
								<DsEmpty>
									Tutti i reparti sono coperti anche a fine stagione.
								</DsEmpty>
							)}
						</DsSection>

						<DsSection
							title="Rosa nelle prossime stagioni"
							hint="Per reparto: chi è ancora sotto contratto a inizio stagione e chi va rinnovato o sostituito"
						>
							<DsChips
								options={planYears.map((y) => ({
									value: y,
									label: y + "/" + String(y + 1).slice(2),
								}))}
								value={year}
								onChange={setPlanYear}
							/>
							<DsTable
								columns={[
									{
										key: "team",
										label: "Squadra",
										render: (r) => <b>{r.team.name}</b>,
									},
									{
										key: "dep",
										label: "Reparto",
										render: (r) => r.department.label,
									},
									{
										key: "staying",
										label: "Sotto contratto",
										align: "center",
										render: (r) => (
											<DsStatus
												status={r.severity}
												label={`${r.staying}/${r.department.min}`}
											/>
										),
									},
									{
										key: "leaving",
										label: "Da rinnovare o sostituire",
										render: (r) =>
											r.leaving.length
												? r.leaving.map(playerName).join(", ")
												: "—",
									},
								]}
								rows={plan}
								empty="Nessuna squadra con almeno 10 giocatori."
							/>
						</DsSection>

						{BUCKETS.map((b) => (
							<DsSection
								key={b.id}
								title={`Scadenze · ${b.label.toLowerCase()}`}
								action={
									b.id === "3m" ? (
										<DsLink to="/manager/players-contracts">
											Tutti i contratti
										</DsLink>
									) : null
								}
							>
								<DsTable
									columns={contractColumns}
									rows={inBucket(b)}
									onRowClick={(c) =>
										navigate(
											"/manager/players-contracts/" + (c.contractId || c.id),
										)
									}
									empty="Nessun contratto in scadenza in questo periodo."
								/>
							</DsSection>
						))}
					</>
				)}
			</div>
		</Page>
	);
}

export default DsMarketPage;
